import React, {useEffect, useState} from 'react';
import {
  Modal,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useAuth } from '../../../context/auth';

// Chute conservador para a altura do teclado do iOS: usado quando o app roda
// dentro de um iframe (embed em app parceiro), onde o iOS não reporta o
// encolhimento do visualViewport do frame ao abrir o teclado.
const IOS_KEYBOARD_FALLBACK_HEIGHT = 250;
const isTextField = (el: EventTarget | null): boolean =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

const Sheet = styled.View`
  background-color: ${({theme}) => theme.colors.background};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  shadow-color: #000;
  shadow-offset: 0px -3px;
  shadow-opacity: 0.12;
  shadow-radius: 12px;
  elevation: 24;
`;

const Handle = styled.View`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background-color: ${({theme}) => theme.colors.border};
`;

interface Props {
  isOpen: boolean;
  children: React.ReactNode;
  onClose: () => void;
  showHandle?: boolean;
  disableKeyboardAvoidance?: boolean;
  aboveSheet?: React.ReactNode;
}

export default function BottomSheet({
  isOpen,
  children,
  onClose,
  showHandle = true,
  disableKeyboardAvoidance = false,
  aboveSheet,
}: Props) {
  const {clientId} = useAuth();
  const insets = useSafeAreaInsets();
  const [modalVisible, setModalVisible] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  useEffect(() => {
    setModalVisible(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (disableKeyboardAvoidance || !modalVisible) { return; }

    const visualViewport = window.visualViewport;
    const initialHeight = visualViewport ? visualViewport.height : window.innerHeight;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

    const handleViewportResize = () => {
      if (!visualViewport) { return; }
      const shrink = initialHeight - visualViewport.height;
      // Só confia no valor do visualViewport se ele de fato encolheu de forma
      // relevante: dentro de um iframe o iOS costuma não reportar o encolhimento,
      // então esse listener nunca dispara e o fallback do focusin assume.
      if (shrink > 80) {
        if (fallbackTimer) { clearTimeout(fallbackTimer); }
        setKeyboardOffset(shrink);
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isTextField(event.target)) { return; }

      fallbackTimer = setTimeout(() => {
        setKeyboardOffset(current =>
          current > 0 ? current : IOS_KEYBOARD_FALLBACK_HEIGHT,
        );
      }, 350);

      (event.target as HTMLElement).scrollIntoView({block: 'center'});
    };

    const handleFocusOut = () => {
      if (fallbackTimer) { clearTimeout(fallbackTimer); }

      setTimeout(() => {
        if (!isTextField(document.activeElement)) {
          setKeyboardOffset(0);
        }
      }, 50);
    };

    visualViewport?.addEventListener('resize', handleViewportResize);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      if (fallbackTimer) { clearTimeout(fallbackTimer); }
      visualViewport?.removeEventListener('resize', handleViewportResize);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      setKeyboardOffset(0);
    };
  }, [disableKeyboardAvoidance, modalVisible]);

  return (
    <Modal
      visible={modalVisible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
      statusBarTranslucent>
      <View style={styles.root}>
        <TouchableWithoutFeedback onPress={onClose}>
          <View style={styles.backdrop} />
        </TouchableWithoutFeedback>

        <View style={styles.keyboardAvoiding}>
          {aboveSheet}
          <Sheet
            style={{
              paddingBottom: Math.max(insets.bottom, 12),
              marginBottom: clientId === 'urbis' ? keyboardOffset : 0,
            }}>
            {showHandle && (
              <View style={styles.handleContainer}>
                <Handle />
              </View>
            )}
            {children}
          </Sheet>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  keyboardAvoiding: {
    width: '100%',
  },
  handleContainer: {
    alignItems: 'center',
    paddingTop: 12,
    paddingBottom: 4,
  },
});
